import { GetServerSideProps, InferGetServerSidePropsType, NextPage } from 'next';

import { EpisodesSection } from '@/components/ui/features/originals/EpisodesSection/EpisodesSection';
import { MaterialsCarousel } from '@/components/ui/features/originals/MaterialsCarousel/MaterialsCarousel';
import { MaterialsSection } from '@/components/ui/features/originals/MaterialsSection/OriginalsMaterials';
import { OriginalsHeader } from '@/components/ui/features/originals/OriginalsHeader/OriginalsHeader';
import { OriginalsPageSeo } from '@/components/ui/features/originals/OriginalsPageSeo/OriginalsPageSeo';
import { RelatedLecturesSection } from '@/components/ui/features/originals/RelatedLecturesSection/RelatedLecturesSection';
import { Layout } from '@/components/ui/shared/layouts/Layout/Layout';
import { ErrorFallback } from '@/components/ui/shared/overlays/ErrorFallback/ErrorFallback';
import { BugsnagErrorBoundary } from '@/components/ui/shared/utilities/BugsnagErrorBoundary/BugsnagErrorBoundary';
import { accessFrom } from '@/constants/custom-request-headers';
import { AccessFrom } from '@/enums/AccessFrom';
import { queryEpisodeDetails, queryEpisodesForSeries, queryEpisodeTranscripts } from '@/queries/originals-episodes';
import { getMaterialsForSeries } from '@/queries/originals-materials';
import { getOriginalSeries } from '@/queries/originals-series';
import { queryOriginalsLectures } from '@/queries/thin-lectures';
import { OriginalsSeries } from '@/resources/OriginalsSeries';
import { generateStore } from '@/utils/generateStore';
import { splitIdAndSlug } from '@/utils/helperFunctions';
import { Hydrate } from '@/utils/swr/Hydrate';

const Originals: NextPage<InferGetServerSidePropsType<typeof getServerSideProps>> = ({
  fallback,
  accessFromLocation,
}) => {
  return (
    <Hydrate fallback={fallback}>
      <Layout accessFrom={accessFromLocation}>
        <OriginalsPageSeo />
        <OriginalsHeader />
        <EpisodesSection />
        <BugsnagErrorBoundary FallbackComponent={ErrorFallback}>
          <MaterialsSection>
            <MaterialsCarousel />
          </MaterialsSection>
        </BugsnagErrorBoundary>
        <BugsnagErrorBoundary FallbackComponent={ErrorFallback}>
          <RelatedLecturesSection />
        </BugsnagErrorBoundary>
      </Layout>
    </Hydrate>
  );
};

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { id: seriesId } = splitIdAndSlug(context.params.id.toString());

  const store = generateStore({ context });

  try {
    const { data } = await store.fetchResource(getOriginalSeries(seriesId));
    const { subject } = data as OriginalsSeries;

    const { data: episodes } = await store.fetchResources(queryEpisodesForSeries(seriesId));

    await store.fetchResources(getMaterialsForSeries(seriesId));
    await store.fetchResources(queryOriginalsLectures(subject.id));

    if (episodes?.[0]?.id) {
      await store.fetchResourceRelationship(queryEpisodeDetails(episodes?.[0]?.id), {
        dontThrowOnError: (res) => res.status === 401,
      });

      await store.fetchResourceRelationship(queryEpisodeTranscripts(episodes?.[0]?.id), {
        dontThrowOnError: (res) => res.status === 401,
      });
    }
  } catch (err) {
    console.log(err);
    return { notFound: true };
  }

  return {
    props: {
      fallback: store.fallback,
      accessFromLocation: context.req.headers[accessFrom] || AccessFrom.Core,
    },
  };
};

export default Originals;
